/**
 * Ripple + shoreline shading for water scene furniture (issue #65). Adds
 * animated ripples and a feathered/depth-tinted shoreline to the flat water
 * caps and draped ribbons built in water.ts, without SSR, caustics or a
 * clipmap (explicitly out of scope) and without any per-frame raycast.
 *
 * Dual-phase flow-map technique (two time-offset, phase-blended fetches of a
 * small tileable gradient texture, cross-faded so neither copy's scroll is
 * ever seen sliding) adapted from Braffolk/fable5-world-demo
 * `src/render/WaterMaterial.ts` (MIT licence, commit fd75fdb) — including its
 * trick of adding a small constant "breeze" vector to the flow so lakes (zero
 * hydrological flow) still drift instead of sitting perfectly static.
 *
 * Everything here is pure geometry/maths (Node-testable) plus one
 * onBeforeCompile hook on a single shared MeshStandardMaterial — normal
 * three.js lighting is kept, we only splice in the ripple normal perturbation
 * and the shoreline feather. Purely cosmetic: never referenced by raypath/
 * (hard constraint #1, guarded by water.guard.test.ts).
 */
import {
  DataTexture,
  DoubleSide,
  MeshStandardMaterial,
  RGBAFormat,
  RepeatWrapping,
  type Material,
  type WebGLProgramParametersWithUniforms,
} from 'three';
import type { LocalXZ } from '../core/coordinates';

/** Bank feather width (m): a vertex this far from the shore reads as full
 * "deep water"; closer than this it fades/darkens toward the bank. */
export const FEATHER_DIST_M = 6;

/** Ripple crossfade cycle length (s) — long enough that the crossfade itself
 * isn't a visible pulse, short enough that the two phases never drift far
 * apart in screen-space. */
export const RIPPLE_PHASE_PERIOD_S = 3.2;

/** Per-kind ripple advection speed (texture-space m/s along flow direction). */
export const RIVER_FLOW_SPEED_MPS = 0.4;
export const STREAM_FLOW_SPEED_MPS = 0.6;

/** Small constant "breeze" drift added to every water surface's flow vector
 * (see module note) — the only motion a lake (zero hydrological flow) gets. */
export const LAKE_DRIFT_VEC: [number, number] = [0.05, 0.035];

export const RIPPLE_TEX_SIZE = 64;

/** Minimum perpendicular distance (m) from point p to the segment a-b. */
export function pointToSegmentDistance2D(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  let t = 0;
  if (lenSq > 0) t = Math.max(0, Math.min(1, ((px - ax) * abx + (pz - az) * abz) / lenSq));
  const dx = px - (ax + abx * t);
  const dz = pz - (az + abz * t);
  return Math.hypot(dx, dz);
}

/** Minimum distance (m) from p to the boundary of a closed ring (wraps the
 * last vertex back to the first). */
export function ringEdgeDistance(ring: LocalXZ[], p: LocalXZ): number {
  let best = Infinity;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    best = Math.min(best, pointToSegmentDistance2D(p.x, p.z, a.x, a.z, b.x, b.z));
  }
  return best;
}

/** 1 at the bank (distance 0), 0 at or beyond featherDistM, linear between. */
export function edgeFactorFromDistance(dist: number, featherDistM = FEATHER_DIST_M): number {
  return 1 - Math.max(0, Math.min(1, dist / featherDistM));
}

function signedArea(ring: LocalXZ[]): number {
  let sum = 0;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    sum += a.x * b.z - b.x * a.z;
  }
  return sum / 2;
}

/**
 * Shrinks a closed ring inward by roughly distM, used to build the shoreline
 * border band (see appendPolygonWithShoreline in water.ts): the band between
 * the original ring and this inset ring gets the bank feather (edge 1 -> 0),
 * the inset polygon's interior stays full "deep water". A simple bevel
 * offset (average of the two adjacent inward edge normals), not a true
 * mitred polygon offset — adequate for small, roughly-convex OSM lake
 * polygons and cheap to compute at build time.
 *
 * Returns null (never a fabricated/self-intersecting shape) when the ring
 * has fewer than 3 points or distM would collapse it (any offset vertex
 * would cross to the wrong side of the ring's own centroid-facing edges).
 */
export function insetRing(ring: LocalXZ[], distM: number): LocalXZ[] | null {
  const n = ring.length;
  if (n < 3) return null;
  const area = signedArea(ring);
  if (area === 0) return null;
  const windingSign = area > 0 ? 1 : -1; // CCW positive area; inward normal sign depends on winding

  const inset: LocalXZ[] = [];
  for (let i = 0; i < n; i++) {
    const prev = ring[(i - 1 + n) % n];
    const curr = ring[i];
    const next = ring[(i + 1) % n];

    const e1x = curr.x - prev.x;
    const e1z = curr.z - prev.z;
    const len1 = Math.hypot(e1x, e1z) || 1;
    const n1x = (-e1z / len1) * windingSign;
    const n1z = (e1x / len1) * windingSign;

    const e2x = next.x - curr.x;
    const e2z = next.z - curr.z;
    const len2 = Math.hypot(e2x, e2z) || 1;
    const n2x = (-e2z / len2) * windingSign;
    const n2z = (e2x / len2) * windingSign;

    let nx = n1x + n2x;
    let nz = n1z + n2z;
    const nLen = Math.hypot(nx, nz);
    if (nLen < 1e-9) continue; // straight-through vertex: no bisector, skip offsetting it
    nx /= nLen;
    nz /= nLen;

    inset.push({ x: curr.x + nx * distM, z: curr.z + nz * distM });
  }
  if (inset.length !== n) return null;

  // Degenerate if any edge reversed direction relative to the source ring —
  // the offset overshot past the polygon's medial axis and folded the ring
  // over itself (a real risk for a bevel offset with no clipping/mitre
  // limit). A valid inward offset preserves every edge's direction.
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const d0x = ring[j].x - ring[i].x;
    const d0z = ring[j].z - ring[i].z;
    const d1x = inset[j].x - inset[i].x;
    const d1z = inset[j].z - inset[i].z;
    if (d0x * d1x + d0z * d1z <= 0) return null;
  }

  return inset;
}

/** Triangle-wave crossfade weight in [0, 1]: 0 at phase start/end, 1 at the
 * halfway point — the classic two-phase flow-map blend. Periodic in period. */
export function phaseWeight(t: number, period: number): number {
  const ph = (t / period) % 1;
  const frac = ph < 0 ? ph + 1 : ph;
  return 1 - Math.abs(frac * 2 - 1);
}

/** The two crossfading phase fractions (always exactly half a cycle apart). */
export function phaseFractions(t: number, period: number): [number, number] {
  const ph1raw = (t / period) % 1;
  const ph1 = ph1raw < 0 ? ph1raw + 1 : ph1raw;
  const ph2 = (ph1 + 0.5) % 1;
  return [ph1, ph2];
}

/**
 * Analytic tileable gradient field: a small sum of integer-frequency sine
 * waves is exactly periodic on [0,1) x [0,1) (u=0 and u=1 land on the same
 * phase), so it bakes into a texture with no visible seam and needs no
 * blending at the tile edge. Doubles as the low-tier fragment-shader formula
 * (see createWaterMaterial) so both tiers read as the same ripple "shape".
 */
export function rippleGradientAt(u: number, v: number): [number, number] {
  const TWO_PI = Math.PI * 2;
  const terms: Array<[number, number, number]> = [
    [3, 2, 1],
    [-5, 3, 0.6],
    [2, -6, 0.5],
    [-4, -1, 0.35],
  ];
  let dx = 0;
  let dy = 0;
  for (const [fx, fy, amp] of terms) {
    const phase = (fx * u + fy * v) * TWO_PI;
    dx += Math.cos(phase) * fx * amp;
    dy += Math.sin(phase) * fy * amp;
  }
  const norm = 1 / terms.reduce((s, [fx, , amp]) => s + Math.abs(fx) * amp, 0);
  return [dx * norm, dy * norm];
}

/** Bakes rippleGradientAt into a size*size*2 (dx, dy per texel) tile. */
export function bakeRippleGradientTile(size: number): Float32Array {
  const data = new Float32Array(size * size * 2);
  for (let j = 0; j < size; j++) {
    for (let i = 0; i < size; i++) {
      const [dx, dy] = rippleGradientAt(i / size, j / size);
      const idx = (j * size + i) * 2;
      data[idx] = dx;
      data[idx + 1] = dy;
    }
  }
  return data;
}

let rippleTextureCache: DataTexture | undefined;

/** Small tileable normal/gradient texture, built once and shared by every
 * water material instance (issue #17: no per-layer texture allocation). */
export function makeRippleTexture(): DataTexture {
  if (rippleTextureCache) return rippleTextureCache;
  const size = RIPPLE_TEX_SIZE;
  const grad = bakeRippleGradientTile(size);
  const rgba = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    rgba[i * 4] = Math.round((grad[i * 2] * 0.5 + 0.5) * 255);
    rgba[i * 4 + 1] = Math.round((grad[i * 2 + 1] * 0.5 + 0.5) * 255);
    rgba[i * 4 + 2] = 128;
    rgba[i * 4 + 3] = 255;
  }
  const tex = new DataTexture(rgba, size, size, RGBAFormat);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.needsUpdate = true;
  rippleTextureCache = tex;
  return tex;
}

export interface WaterMaterialOptions {
  /** High tier: two texture fetches drive the ripple normal. Low tier: the
   * same crossfade math runs on an analytic sine field instead (issue #17 —
   * keeps the animation, drops the texture fetch). */
  rippleTexture: boolean;
  color?: number;
  opacity?: number;
}

const DEFAULT_COLOR = 0x3b6ea5;
const DEFAULT_OPACITY = 0.62;

/**
 * Builds the single shared water MeshStandardMaterial (one material for
 * every water mesh — draw-call count is unaffected by this feature). Normal
 * PBR lighting is untouched; onBeforeCompile only splices in:
 *  - a per-vertex `aEdge` (signed lateral param for ribbons / bank-distance
 *    feather for cap border bands) and `aFlow` (per-vertex advection vector)
 *  - a fragment-stage ripple normal perturbation (dual-phase texture or
 *    analytic fallback)
 *  - a shoreline feather/darken on `diffuseColor` driven by `aEdge`
 */
export function createWaterMaterial(options: WaterMaterialOptions): MeshStandardMaterial {
  const material = new MeshStandardMaterial({
    color: options.color ?? DEFAULT_COLOR,
    transparent: true,
    opacity: options.opacity ?? DEFAULT_OPACITY,
    roughness: 0.15,
    metalness: 0,
    side: DoubleSide,
    depthWrite: false,
  });

  material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uTime = { value: 0 };
    shader.uniforms.uRippleScale = { value: 0.35 };
    shader.uniforms.uRippleStrength = { value: options.rippleTexture ? 0.12 : 0.08 };
    shader.uniforms.uRipplePeriod = { value: RIPPLE_PHASE_PERIOD_S };
    shader.uniforms.uShoreTint = { value: { r: 0.11, g: 0.18, b: 0.13 } };
    if (options.rippleTexture) {
      shader.uniforms.uRippleTex = { value: makeRippleTexture() };
    }

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute float aEdge;
attribute vec2 aFlow;
varying float vEdgeSigned;
varying vec2 vFlow;
varying vec2 vRippleXZ;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vEdgeSigned = aEdge;
vFlow = aFlow;
vRippleXZ = ( modelMatrix * vec4( transformed, 1.0 ) ).xz;`,
      );

    const tiltFetch = options.rippleTexture
      ? `tiltA = texture2D( uRippleTex, uvA ).xy * 2.0 - 1.0;
  tiltB = texture2D( uRippleTex, uvB ).xy * 2.0 - 1.0;`
      : `tiltA = vec2( sin( uvA.x * 6.283185 + uvA.y * 3.141593 ), cos( uvA.y * 6.283185 - uvA.x * 3.141593 ) );
  tiltB = vec2( sin( uvB.x * 6.283185 + uvB.y * 3.141593 ), cos( uvB.y * 6.283185 - uvB.x * 3.141593 ) );`;

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float uTime;
uniform float uRippleScale;
uniform float uRippleStrength;
uniform float uRipplePeriod;
uniform vec3 uShoreTint;
${options.rippleTexture ? 'uniform sampler2D uRippleTex;' : ''}
varying float vEdgeSigned;
varying vec2 vFlow;
varying vec2 vRippleXZ;`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
{
  vec2 driftFlow = vFlow + vec2( ${LAKE_DRIFT_VEC[0].toFixed(4)}, ${LAKE_DRIFT_VEC[1].toFixed(4)} );
  float ph1 = fract( uTime / uRipplePeriod );
  float ph2 = fract( ph1 + 0.5 );
  float w = 1.0 - abs( ph1 * 2.0 - 1.0 );
  vec2 uvA = vRippleXZ * uRippleScale - driftFlow * ( ph1 * uRipplePeriod );
  vec2 uvB = vRippleXZ * uRippleScale - driftFlow * ( ph2 * uRipplePeriod ) + vec2( 13.1, 47.7 );
  vec2 tiltA;
  vec2 tiltB;
  ${tiltFetch}
  vec2 tilt = mix( tiltA, tiltB, w ) * uRippleStrength;
  // mat3( viewMatrix ), not three's normal matrix: that one is declared in
  // the vertex stage only, so referencing it here is an undeclared identifier
  // and the whole material fails to compile. viewMatrix rotates the
  // world-space tilt into the view space that the normal lives in.
  normal = normalize( normal + mat3( viewMatrix ) * vec3( tilt.x, 0.0, tilt.y ) );
}`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
{
  float shoreEdge = clamp( abs( vEdgeSigned ), 0.0, 1.0 );
  float feather = 1.0 - smoothstep( 0.0, 1.0, shoreEdge );
  diffuseColor.rgb = mix( diffuseColor.rgb, uShoreTint, shoreEdge );
  diffuseColor.a *= feather;
}`,
      );

    material.userData.shader = shader;
  };

  return material;
}

/** Advances the shared ripple time uniform. A no-op before the material has
 * compiled once (userData.shader is only populated by onBeforeCompile). */
export function setWaterMaterialTime(material: Material, t: number): void {
  const shader = material.userData.shader as WebGLProgramParametersWithUniforms | undefined;
  if (shader?.uniforms.uTime) shader.uniforms.uTime.value = t;
}
